import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Clock, MapPin, Bell, LogOut, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function WaitlistStatus() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRegions, setActiveRegions] = useState([]);

  useEffect(() => {
    checkAuth();
    fetchActiveRegions();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      // If user is not waitlisted, redirect to appropriate dashboard
      if (response.data.region_status !== 'waitlisted') {
        const dashboardPath = response.data.user_type === 'tradesperson' 
          ? '/tradesperson/dashboard' 
          : response.data.user_type === 'employer'
          ? '/employer/dashboard'
          : '/admin/dashboard';
        navigate(dashboardPath);
        return;
      }
      
      setUser(response.data);
    } catch (error) {
      // Not logged in - show generic waitlist confirmation
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveRegions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/regions/active`);
      setActiveRegions(response.data.regions || []);
    } catch (error) {
      console.error('Failed to fetch active regions');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      console.error('Logout failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="h-8 w-8 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900">OnsiteHand</h1>
            </div>
            {user && (
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="h-5 w-5" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 border border-slate-200 text-center">
          {/* Icon */}
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="h-10 w-10 text-amber-600" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-slate-900 mb-4" data-testid="waitlist-title">
            You're on the Waitlist
          </h1>

          {/* Message */}
          <p className="text-slate-600 mb-6">
            {user ? (
              <>
                Thanks for signing up, <span className="font-medium">{user.name}</span>! 
                We're not active in your area just yet, but we're expanding fast.
              </>
            ) : (
              <>
                Thanks for your interest in OnsiteHand! 
                We're not active in your area just yet, but we're expanding fast.
              </>
            )}
          </p>

          {/* What happens next */}
          <div className="bg-slate-50 rounded-lg p-6 mb-6 text-left">
            <h3 className="font-semibold text-slate-900 mb-3">What happens next?</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Bell className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-600 text-sm">
                  We'll email you as soon as we launch in your area
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-600 text-sm">
                  Your account details are saved – you won't need to register again
                </span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-600 text-sm">
                  Once activated, you'll have full access to the platform
                </span>
              </li>
            </ul>
          </div>

          {/* Current Active Regions */}
          {activeRegions.length > 0 && (
            <div className="bg-green-50 rounded-lg p-6 mb-6 text-left">
              <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Currently Active Areas
              </h3>
              <div className="flex flex-wrap gap-2">
                {activeRegions.map((region) => (
                  <span
                    key={region.region_id}
                    className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm"
                  >
                    {region.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* User Info */}
          {user && (
            <div className="border-t border-slate-200 pt-6 mt-6">
              <p className="text-sm text-slate-500">
                Registered email: <span className="font-medium text-slate-700">{user.email}</span>
              </p>
              {user.postcode && (
                <p className="text-sm text-slate-500 mt-1">
                  Your postcode: <span className="font-medium text-slate-700">{user.postcode}</span>
                </p>
              )}
            </div>
          )}

          {/* Back to Home */}
          <button
            onClick={() => navigate('/')}
            className="btn-secondary mt-6"
            data-testid="back-home-btn"
          >
            Back to Homepage
          </button>
        </div>
      </main>
    </div>
  );
}
